import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, CheckboxGroup, Select, Spinner, Table, TextInput } from '@opendate-app/setlist';

const axios = require('axios').default;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const BLANK_COMPONENT = { name: '', amount: '', general_ledger_code_id: '' };
const BLANK_CODE = { code: '', name: '' };
const NEW_CODE_VALUE = '__new__';
const DRAFT_ROW = { id: '__draft__' };
export const PRICE_BANDS_MODE = 'bands';
export const FEE_COMPONENTS_MODE = 'components';
const mutedTextStyle = { color: 'var(--sl-text-tertiary)' };

// Where the components and account code live. A confirm saves each change as it
// is made; a series wizard passes its own store that edits the unsaved plan.
export const confirmGeneralLedgerStore = (team, confirm) => {
  const componentsPath = `/teams/${team.id}/calendar_events/${confirm.id}/organizer_fee_components`;
  return {
    listComponents: () => axios.get(componentsPath).then(res => res.data || []),
    createComponent: (attributes) => axios.post(componentsPath, { organizer_fee_component: attributes }),
    destroyComponent: (component) => axios.delete(`${componentsPath}/${component.id}`),
    setAccountCode: (value) => axios.patch(`/teams/${team.id}/confirms/${confirm.id}`, {
      confirm: { general_ledger_code_id: value === '' ? null : value },
    }),
    setOrganizerFeeCode: (value) => axios.patch(`/teams/${team.id}/confirms/${confirm.id}`, {
      confirm: { organizer_fee_general_ledger_code_id: value === '' ? null : value },
    }),
  };
};

const EventGeneralLedgerSettings = ({ csrfToken, confirm, team, onComponentsChange, onModeChange, store, forSeries = false }) => {
  const [generalLedgerCodes, setGeneralLedgerCodes] = useState([]);
  const [components, setComponents] = useState([]);
  const [draft, setDraft] = useState(BLANK_COMPONENT);
  const [accountCodeId, setAccountCodeId] = useState(
    confirm.general_ledger_code_id ? String(confirm.general_ledger_code_id) : ''
  );
  const [bandCodeId, setBandCodeId] = useState(
    confirm.organizer_fee_general_ledger_code_id ? String(confirm.organizer_fee_general_ledger_code_id) : ''
  );
  const [mode, setMode] = useState(forSeries ? FEE_COMPONENTS_MODE : null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [didFailToLoad, setDidFailToLoad] = useState(false);
  const [newCodeTarget, setNewCodeTarget] = useState(null);
  const [newCode, setNewCode] = useState(BLANK_CODE);
  const isCreatingCode = useRef(false);

  const defaultStore = useMemo(() => confirmGeneralLedgerStore(team, confirm), [team.id, confirm.id]);
  const data = store || defaultStore;
  const subject = forSeries ? 'series' : 'show';

  const changeMode = (nextMode) => {
    setMode(nextMode);
    if (onModeChange) onModeChange(nextMode);
  };

  const showError = (err) => {
    const body = err?.response?.data;
    toast.error(Array.isArray(body) ? body.join(', ') : 'Something went wrong. Try again.', {
      position: toast.POSITION.TOP_CENTER,
      draggable: false,
      closeOnClick: false,
      autoClose: 5000,
      hideProgressBar: true,
    });
  };

  const loadComponents = (isIgnored = () => false) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    if (onComponentsChange) onComponentsChange(null);
    return data.listComponents()
      .then(loaded => {
        if (isIgnored()) return;
        setComponents(loaded);
        setDidFailToLoad(false);
        if (onComponentsChange) onComponentsChange(loaded.length);
        if (!forSeries && loaded.length > 0) changeMode(FEE_COMPONENTS_MODE);
        if (!forSeries && mode === null && loaded.length === 0) changeMode(PRICE_BANDS_MODE);
      })
      .catch(err => {
        if (isIgnored()) return;
        setDidFailToLoad(true);
        showError(err);
      });
  };

  const handleRetryLoad = () => {
    setIsProcessing(true);
    loadComponents().then(() => setIsProcessing(false));
  };

  useEffect(() => {
    let ignore = false;
    const isIgnored = () => ignore;

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    Promise.all([
      axios.get(`/teams/${team.id}/general_ledger_codes`)
        .then(res => {
          if (ignore) return;
          setGeneralLedgerCodes(res.data || []);
        })
        .catch(err => {
          if (ignore) return;
          showError(err);
        }),
      loadComponents(isIgnored),
    ]).then(() => {
      if (ignore) return;
      setIsLoading(false);
    });

    return () => { ignore = true; };
  }, [team.id, confirm.id, csrfToken]);

  const feeTotal = components.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);

  const codeLabel = (id) => {
    const found = generalLedgerCodes.find(code => String(code.id) === String(id));
    return found ? `${found.code} — ${found.name}` : '—';
  };

  const assignCode = (target, value) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    if (target === 'band') {
      return data.setOrganizerFeeCode(value).then(() => setBandCodeId(value));
    }
    return data.setAccountCode(value).then(() => setAccountCodeId(value));
  };

  const handleSavedCodeChange = (target, value) => {
    setIsProcessing(true);
    return assignCode(target, value)
      .catch(showError)
      .then(() => setIsProcessing(false));
  };

  const handleCodeSelect = (target, value) => {
    if (value === NEW_CODE_VALUE) {
      setNewCodeTarget(target);
      return;
    }

    if (target === 'account') {
      if (value === accountCodeId) return;
      if (value === '' && !window.confirm(`Remove the account code from this ${subject}?`)) return;

      handleSavedCodeChange('account', value);
    } else if (target === 'band') {
      if (value === bandCodeId) return;
      if (value === '' && !window.confirm(`Remove the organizer fee GL code from this ${subject}?`)) return;

      handleSavedCodeChange('band', value);
    } else {
      setDraft({ ...draft, general_ledger_code_id: value });
    }
  };

  const handleCreateCode = () => {
    if (isCreatingCode.current) return;

    isCreatingCode.current = true;
    setIsProcessing(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.post(`/teams/${team.id}/general_ledger_codes`, {
      general_ledger_code: { code: newCode.code, name: newCode.name },
    })
      .then(res => {
        const created = res.data;
        setGeneralLedgerCodes(codes => [...codes, created]
          .sort((a, b) => String(a.code).localeCompare(String(b.code))));

        if (newCodeTarget === 'fee') {
          setDraft(current => ({ ...current, general_ledger_code_id: String(created.id) }));
          return null;
        }

        return assignCode(newCodeTarget, String(created.id));
      })
      .then(() => {
        setNewCode(BLANK_CODE);
        setNewCodeTarget(null);
      })
      .catch(showError)
      .then(() => {
        isCreatingCode.current = false;
        setIsProcessing(false);
      });
  };

  const handleAdd = () => {
    setIsProcessing(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    data.createComponent({
      name: draft.name,
      amount: draft.amount,
      general_ledger_code_id: draft.general_ledger_code_id === ''
        ? null
        : draft.general_ledger_code_id,
    })
      .then(() => {
        setDraft(BLANK_COMPONENT);
        return loadComponents();
      })
      .catch(showError)
      .then(() => setIsProcessing(false));
  };

  const handleRemove = (component) => {
    setIsProcessing(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    data.destroyComponent(component)
      .then(() => loadComponents())
      .catch(showError)
      .then(() => setIsProcessing(false));
  };

  const canAdd = draft.name.trim() !== '' && draft.amount !== '' && !isProcessing;

  const codeOptions = (emptyLabel) => [
    { value: '', label: emptyLabel },
    ...generalLedgerCodes.map(code => ({ value: String(code.id), label: `${code.code} — ${code.name}` })),
    { value: NEW_CODE_VALUE, label: '+ Add a new GL code…' },
  ];

  const isDraft = (row) => row === DRAFT_ROW;

  const columns = [
    {
      key: 'name',
      header: 'Fee Name',
      render: (row) => (isDraft(row) ? (
        <TextInput
          size="sm"
          aria-label="Fee name"
          placeholder="facility fee"
          value={draft.name}
          disabled={isProcessing}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
        />
      ) : row.name),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (row) => (isDraft(row) ? (
        <TextInput
          size="sm"
          aria-label="Fee amount"
          type="number"
          min="0"
          step="0.01"
          placeholder="2.00"
          value={draft.amount}
          disabled={isProcessing}
          onChange={e => setDraft({ ...draft, amount: e.target.value })}
        />
      ) : currencyFormatter.format(parseFloat(row.amount || 0))),
    },
    {
      key: 'code',
      header: 'GL Code',
      render: (row) => (isDraft(row) ? (
        <Select
          size="sm"
          id="new-fee-general-ledger-code"
          aria-label="Fee GL code"
          options={codeOptions('No GL code')}
          value={draft.general_ledger_code_id}
          disabled={isProcessing}
          onChange={value => handleCodeSelect('fee', value)}
        />
      ) : codeLabel(row.general_ledger_code_id)),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (isDraft(row) ? (
        <Button variant="primary" size="sm" disabled={!canAdd} onClick={handleAdd}>
          Add Fee
        </Button>
      ) : (
        <Button variant="link-destructive" size="sm" disabled={isProcessing} onClick={() => handleRemove(row)}>
          Remove
        </Button>
      )),
    },
  ];

  const newCodeForm = (
    <div
      className="p-3 mb-3"
      style={{
        maxWidth: '520px',
        border: '1px solid var(--sl-border-secondary)',
        borderRadius: '8px',
        background: 'var(--sl-bg-primary)',
      }}
    >
      <p className="small mb-2">
        <strong>New GL code</strong> — this is added to your chart of accounts and
        becomes available on every event.
      </p>
      <div className="d-flex" style={{ gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <TextInput
            size="sm"
            id="new-general-ledger-code"
            label="Code"
            placeholder="54206"
            value={newCode.code}
            disabled={isProcessing}
            onChange={e => setNewCode({ ...newCode, code: e.target.value })}
          />
        </div>
        <div style={{ flex: 1 }}>
          <TextInput
            size="sm"
            id="new-general-ledger-name"
            label="Name"
            placeholder="Facility Fee Income"
            value={newCode.name}
            disabled={isProcessing}
            onChange={e => setNewCode({ ...newCode, name: e.target.value })}
          />
        </div>
      </div>
      <div className="d-flex mt-2" style={{ gap: '8px' }}>
        <Button
          variant="primary"
          size="sm"
          disabled={
            isProcessing ||
            newCode.code.trim() === '' ||
            newCode.name.trim() === ''
          }
          onClick={handleCreateCode}
        >
          Add Code
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          disabled={isProcessing}
          onClick={() => {
            setNewCode(BLANK_CODE);
            setNewCodeTarget(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: '40px', color: 'var(--sl-text-primary)' }}>
      <div className="row">
        <div className="col-12 col-md">
          <p className="mb-2"><strong>General Ledger</strong></p>
          <p className="small mb-1" style={mutedTextStyle}>
            The account this {subject}&apos;s ticket revenue posts to, and the fees it charges.
          </p>
          {forSeries ? (
            <p className="small mb-1" style={mutedTextStyle}>
              Fees entered here are what the buyer pays on every ticket, on every date in the series, and they replace the fees on the venue&apos;s price bands. Leave them empty to charge the band fees instead.
            </p>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center">
          <Spinner aria-label="Loading general ledger settings" />
        </div>
      ) : (
        <div className="row">
          <div className="col-12 col-md">
            <div className="mt-3 mb-3" style={{ maxWidth: '320px' }}>
              <Select
                size="sm"
                id="event-account-code"
                label="Account Code"
                disabled={isProcessing}
                options={codeOptions('No account code')}
                value={accountCodeId}
                onChange={value => handleCodeSelect('account', value)}
              />
            </div>

            {newCodeTarget === 'account' ? newCodeForm : null}

            {forSeries || didFailToLoad ? null : (
              <div className="mt-3 mb-3" style={{ maxWidth: '520px' }}>
                <p className="mb-2" style={{ fontSize: '14px', fontWeight: 500 }} id="organizer-fee-posting-label">
                  Organizer Fee Posting
                </p>
                <CheckboxGroup
                  type="radio"
                  size="sm"
                  name="organizer-fee-general-ledger-mode"
                  aria-labelledby="organizer-fee-posting-label"
                  value={mode}
                  disabled={isProcessing}
                  onChange={changeMode}
                  items={[
                    {
                      value: PRICE_BANDS_MODE,
                      label: 'Price band fees',
                      supportingText: components.length > 0
                        ? "Remove this show's fee components to charge the price band fees instead."
                        : 'Charge the price band fees below and post them all to one GL code.',
                      disabled: components.length > 0,
                    },
                    {
                      value: FEE_COMPONENTS_MODE,
                      label: 'Fee components',
                      supportingText: 'Replace the price band fees with fees you list here, each posting to its own GL code.',
                    },
                  ]}
                />
              </div>
            )}

            {!forSeries && !didFailToLoad && mode === PRICE_BANDS_MODE ? (
              <>
                <div className="mb-3" style={{ maxWidth: '320px' }}>
                  <Select
                    size="sm"
                    id="event-organizer-fee-code"
                    label="Organizer Fee GL Code"
                    disabled={isProcessing}
                    options={codeOptions('No GL code')}
                    value={bandCodeId}
                    onChange={value => handleCodeSelect('band', value)}
                  />
                </div>
                {newCodeTarget === 'band' ? newCodeForm : null}
              </>
            ) : null}

            {didFailToLoad ? (
              <div className="mt-3" style={{ maxWidth: '520px' }}>
                <Alert
                  color="warning"
                  title="This show's fees could not be loaded."
                  description="Until they load we don't know whether this show charges its own fees, so the organizer fees below stay locked rather than risk showing you the wrong ones."
                  actions={
                    <Button variant="secondary" size="sm" disabled={isProcessing} onClick={handleRetryLoad}>
                      Try Again
                    </Button>
                  }
                />
              </div>
            ) : mode === FEE_COMPONENTS_MODE ? (
              <>
              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                <Table
                  columns={columns}
                  data={[...components, DRAFT_ROW]}
                  getRowKey={row => row.id}
                  stackBelow={640}
                />
              </div>

              {newCodeTarget === 'fee' ? newCodeForm : null}

              {components.length > 0 ? (
                <p className="small mb-1">
                  <strong>
                    Organizer fee for {forSeries ? 'each date' : 'this show'}: {currencyFormatter.format(feeTotal)}
                  </strong>
                </p>
              ) : null}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

EventGeneralLedgerSettings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  onComponentsChange: PropTypes.func,
  onModeChange: PropTypes.func,
  store: PropTypes.shape({
    listComponents: PropTypes.func.isRequired,
    createComponent: PropTypes.func.isRequired,
    destroyComponent: PropTypes.func.isRequired,
    setAccountCode: PropTypes.func.isRequired,
    setOrganizerFeeCode: PropTypes.func,
  }),
  forSeries: PropTypes.bool,
};

export default EventGeneralLedgerSettings;
